import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import PermissionGuard from '../../components/PermissionGuard';
import { usePermissions } from '../../hooks/usePermissions';
import { getIcon } from '../../utils/iconMapper';
import SucursalForm from './SucursalForm';
import { sucursalesService } from '../../services/sucursalesService';
import { empresasService } from '../../services/empresasService';
import SnowFallComponent from 'components/SnowFallComponent';

function Sucursales() {
  const { canView, canCreate, canUpdate, canDelete } = usePermissions();
  const [sucursales, setSucursales] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSucursal, setEditingSucursal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sucursalToDelete, setSucursalToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filtroEmpresa, setFiltroEmpresa] = useState('');

  // Declarar funciones antes de usarlas en useEffect
  const loadEmpresas = async () => {
    try {
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const normalized = empresasData.map(emp => ({
          EmpresaID: emp.EmpresaID || emp.empresaID,
          RazonSocial: emp.RazonSocial || emp.razonSocial || '',
        }));
        setEmpresas(normalized);
      }
    } catch (err) {
    }
  };

  const loadSucursales = async () => {
    try {
      setLoading(true);
      setError('');
      const empresaId = filtroEmpresa ? parseInt(filtroEmpresa) : null;
      const response = await sucursalesService.getAll(empresaId);
      if (response.success && response.data) {
        const sucursalesData = Array.isArray(response.data) ? response.data : [];
        // Normalizar los datos para manejar tanto camelCase como PascalCase
        const normalizedData = sucursalesData.map(suc => ({
          SucursalID: suc.SucursalID || suc.sucursalID,
          EmpresaID: suc.EmpresaID || suc.empresaID,
          Nombre: suc.Nombre || suc.nombre || '',
          Direccion: suc.Direccion || suc.direccion,
          Telefono: suc.Telefono || suc.telefono,
          Ciudad: suc.Ciudad || suc.ciudad,
          CodigoPostal: suc.CodigoPostal || suc.codigoPostal,
          Zona: suc.Zona || suc.zona,
          Activo: suc.Activo !== undefined ? suc.Activo : (suc.activo !== undefined ? suc.activo : true),
        }));
        setSucursales(normalizedData);
      } else {
        setError(response.Message || response.message || 'Error al cargar sucursales');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks después de las declaraciones de funciones
  useEffect(() => {
    loadEmpresas();
    loadSucursales();
  }, []);

  useEffect(() => {
    loadSucursales();
  }, [filtroEmpresa]);

  // Verificar si el usuario tiene permiso para ver este módulo (después de todos los hooks)
  if (!canView('Sucursales') && !canView('Configuración')) {
    return (
      <div className="content">
        <SnowFallComponent />
        <Alert color="warning">
          No tiene permisos para acceder a este módulo.
        </Alert>
      </div>
    );
  }

  const handleCreate = () => {
    setEditingSucursal(null);
    setShowForm(true);
  };

  const handleEdit = async (sucursal) => {
    try {
      const sucursalId = sucursal.SucursalID || sucursal.sucursalID;
      const response = await sucursalesService.getById(sucursalId);
      if (response.success && response.data) {
        setEditingSucursal(response.data);
        setShowForm(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar los datos de la sucursal');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    }
  };

  const handleDelete = (sucursal) => {
    setSucursalToDelete(sucursal);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!sucursalToDelete) return;

    try {
      setDeleting(true);
      const sucursalId = sucursalToDelete.SucursalID || sucursalToDelete.sucursalID;
      const response = await sucursalesService.delete(sucursalId);
      if (response.success) {
        setSuccess('Sucursal desactivada exitosamente');
        setShowDeleteModal(false);
        setSucursalToDelete(null);
        loadSucursales();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || response.message || 'Error al desactivar sucursal');
      }
    } catch (err) {
      setError(err.message || 'Error de conexión al desactivar sucursal');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSucursal(null);
    setSuccess(editingSucursal ? 'Sucursal actualizada exitosamente' : 'Sucursal creada exitosamente');
    loadSucursales();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSucursal(null);
  };

  const getEmpresaNombre = (empresaID) => {
    const empresa = empresas.find(emp => emp.EmpresaID === empresaID);
    return empresa ? empresa.RazonSocial : '-';
  };

  const columns = [
    {
      key: 'empresa',
      label: 'Empresa',
      accessor: 'EmpresaID',
      render: (value) => getEmpresaNombre(value),
    },
    {
      key: 'nombre',
      label: 'Nombre',
      accessor: 'Nombre',
    },
    {
      key: 'direccion',
      label: 'Dirección',
      accessor: 'Direccion',
      render: (value) => value || '-',
    },
    {
      key: 'ciudad',
      label: 'Ciudad',
      accessor: 'Ciudad',
      render: (value) => value || '-',
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      accessor: 'Telefono',
      render: (value) => value || '-',
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'secondary'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => (
        <div>
          <PermissionGuard modulo="Sucursales" accion="Update">
            <Button
              color="info"
              size="sm"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
            >
              <i className={getIcon("tim-icons icon-pencil")} />
            </Button>
          </PermissionGuard>
          <PermissionGuard modulo="Sucursales" accion="Delete">
            <Button
              color="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
            >
              <i className={getIcon("tim-icons icon-simple-remove")} />
            </Button>
          </PermissionGuard>
        </div>
      ),
    },
  ];

  if (showForm) {
    return (
      <div className="content">
        <SnowFallComponent />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  {editingSucursal ? 'Editar Sucursal' : 'Nueva Sucursal'}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <SucursalForm
                  sucursal={editingSucursal}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Sucursales</CardTitle>
                </Col>
                <Col className="text-right">
                  <PermissionGuard modulo="Sucursales" accion="Create">
                    <Button color="primary" onClick={handleCreate}>
                      <i className={getIcon("tim-icons icon-simple-add")} /> Nueva Sucursal
                    </Button>
                  </PermissionGuard>
                </Col>
              </Row>
              <Row className="mt-3">
                {empresas.length > 1 && (
                  <Col md="4">
                    <select
                      className="form-control"
                      value={filtroEmpresa}
                      onChange={(e) => setFiltroEmpresa(e.target.value)}
                    >
                      <option value="">Todas las empresas</option>
                      {empresas.map((emp) => (
                        <option key={emp.EmpresaID} value={emp.EmpresaID}>
                          {emp.RazonSocial}
                        </option>
                      ))}
                    </select>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={sucursales} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setSucursalToDelete(null);
        }}
        title="Desactivar Sucursal"
        message={`¿Está seguro de desactivar la sucursal "${sucursalToDelete?.Nombre || sucursalToDelete?.nombre}"?`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Sucursales;

